import Papa from 'papaparse';
import { marked } from 'marked';

/**
 * Parse CSV data into headers and body format
 * Reason: Use PapaParse for robust CSV parsing
 */
const parseCSV = (csvString) => {
  const result = Papa.parse(csvString.trim(), { header: true });
  const headers = result.meta.fields;
  const body = result.data.map(row => headers.map(header => row[header] || ''));
  return { headers, body };
};

/**
 * Split delimited text into records, honoring RFC-4180-style double quotes.
 * A quoted field may contain the delimiter, newlines, and escaped quotes (`""`).
 * Reason (T-014 item B9): Excel copies multiline cells as `"line1\nline2"` — a
 * naive split on `\n`/`\t` shreds them into bogus extra rows/columns.
 *
 * @param {string} input
 * @param {string} delimiter - single char (e.g. '\t' or ',')
 * @returns {string[][]} rows of raw (untrimmed) field values
 */
const splitDelimited = (input, delimiter) => {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const s = String(input).replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  for (let i = 0; i < s.length; i += 1) {
    const ch = s[i];
    if (inQuotes) {
      if (ch === '"') {
        if (s[i + 1] === '"') { field += '"'; i += 1; } // escaped quote
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += ch;
    }
  }
  row.push(field);
  rows.push(row);

  // Drop a trailing empty record produced by a final newline.
  return rows.filter((r) => !(r.length === 1 && r[0] === ''));
};

/**
 * Parse TSV (Tab-Separated Values) data
 * Reason: Excel and Google Sheets copy data as TSV by default
 */
const parseTSV = (tsvString) => {
  const rows = splitDelimited(tsvString, '\t');
  if (rows.length === 0) {
    return { headers: [], body: [] };
  }

  const headers = rows[0].map(h => h.trim());
  const body = rows.slice(1).map(row => row.map(cell => cell.trim()));

  return { headers, body };
};

const parseJSON = (jsonString) => {
  try {
    const data = JSON.parse(jsonString);
    if (!Array.isArray(data) || data.length === 0) {
      return { headers: [], body: [] };
    }
    const headers = Object.keys(data[0]);
    const body = data.map(row => headers.map(header => row[header]));
    return { headers, body };
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return { headers: [], body: [] };
  }
};

/**
 * Plain text of a marked table-cell token, with inline markdown stripped.
 *
 * Reason (T-017 / Complaint A): marked keeps the RAW markdown source in
 * `cell.text` (e.g. `"**Ana**"`, `"*activo*"`, `` "`code`" ``). Using it
 * verbatim leaks literal `**` / `*` / `` ` `` asterisks and backticks into the
 * inserted table cells. marked's inline `cell.tokens` carry the decoded LEAF
 * text of each mark, so we walk them and concatenate to get clean plain text
 * (bold/italic/code markers dropped, content preserved).
 *
 * @param {{ text?: string, tokens?: Array }} cell
 * @returns {string}
 */
export const cellPlainText = (cell) => {
  const walk = (tokens) =>
    (tokens || [])
      .map((tok) => (tok && tok.tokens && tok.tokens.length ? walk(tok.tokens) : (tok?.text ?? '')))
      .join('');
  if (cell && Array.isArray(cell.tokens) && cell.tokens.length) {
    return walk(cell.tokens);
  }
  return cell?.text ?? '';
};

const parseMarkdown = (markdownString) => {
    const tokens = marked.lexer(markdownString);
    const tableToken = tokens.find(token => token.type === 'table');

    if (!tableToken) {
        return { headers: [], body: [] };
    }

    const headers = tableToken.header.map(cellPlainText);
    const body = tableToken.rows.map(row => row.map(cellPlainText));

    return { headers, body };
};


/**
 * Read a table cell element into `{ text, colspan, rowspan }`.
 * Reason (T-014 item B8): honor merges. prosemirror-tables represents merged
 * cells as a single cell node carrying colspan/rowspan attrs (spanned positions
 * are simply absent from later rows) — exactly the shape the HTML already has,
 * so we carry the attrs straight through rather than expanding the grid.
 */
const readCell = (el) => ({
  text: el.textContent.trim(),
  colspan: parseInt(el.getAttribute('colspan'), 10) || 1,
  rowspan: parseInt(el.getAttribute('rowspan'), 10) || 1,
});

/** Direct td/th children of a row (ignores nested-table cells). */
const rowCells = (tr) =>
  Array.from(tr.children).filter(c => /^(td|th)$/i.test(c.tagName));

/**
 * Parse HTML table data
 * Reason: Handle tables from various sources (Excel, Google Sheets, web pages)
 * Supports tables with or without <th> headers, and preserves colspan/rowspan.
 */
const parseHTML = (htmlString) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  const table = doc.querySelector('table');
  if (!table) {
    return { headers: [], body: [] };
  }

  // All non-empty rows in document order. (Browsers/jsdom auto-wrap loose rows
  // in an implicit <tbody>, so a plain `tbody tr` query returns the header row
  // too — we identify the header row explicitly and exclude it from the body to
  // avoid duplicating it.)
  const allRows = Array.from(table.querySelectorAll('tr')).filter(tr => rowCells(tr).length > 0);

  // Identify the header row: the row of the first <thead> <th>, else a first
  // row whose cells are ALL <th>.
  let headerRowEl = null;
  const theadTh = table.querySelector('thead th');
  if (theadTh) {
    headerRowEl = theadTh.closest('tr');
  } else if (allRows.length > 0) {
    const first = allRows[0];
    const cells = rowCells(first);
    if (cells.length > 0 && cells.every(c => c.tagName.toLowerCase() === 'th')) {
      headerRowEl = first;
    }
  }

  let headers = headerRowEl ? rowCells(headerRowEl).map(readCell) : [];
  const bodyRows = allRows.filter(tr => tr !== headerRowEl);
  const body = bodyRows.map(tr => rowCells(tr).map(readCell));

  // If no headers found, generate default headers based on column count
  if (headers.length === 0 && body.length > 0) {
    const colCount = body[0].length;
    headers = Array.from({ length: colCount }, (_, i) => `Columna ${i + 1}`);
  }

  return { headers, body };
};

/**
 * Parse table data based on format type
 * Reason: Central function to handle all table formats
 */
export const parseTableData = (data, dataType) => {
  switch (dataType) {
    case 'csv':
      return parseCSV(data);
    case 'tsv':
      return parseTSV(data);
    case 'json':
      return parseJSON(data);
    case 'markdown':
    case 'md':
      return parseMarkdown(data);
    case 'html':
      return parseHTML(data);
    default:
      return { headers: [], body: [] };
  }
};

/**
 * Detect table format from pasted/inserted text.
 * Reason: Auto-detect format to provide seamless paste experience.
 * Priority: HTML > JSON > Markdown > TSV > CSV.
 *
 * IMPORTANT (T-018): a "table" verdict is returned ONLY for genuinely tabular
 * text. Prose — even prose containing commas — returns null so the caller pastes
 * it as normal text WITHOUT the convert-to-table dialog, and a message that mixes
 * a table with surrounding prose returns null so it is rendered as markdown
 * (paragraphs + a native table) instead of being shredded by a single parser.
 *   - MD  : header row + a valid GFM separator row AND every non-empty line is a
 *           table row (a `|`-bearing line). Leading/trailing prose ⇒ not pure ⇒ null.
 *   - TSV : ≥2 lines and EVERY non-empty line contains at least one tab.
 *   - CSV : ≥2 lines, EVERY line has a comma, a consistent field count ≥2 across
 *           ≥80% of lines, and the block does not read as prose (majority of lines
 *           ending in sentence punctuation `.!?:;` ⇒ prose ⇒ null).
 */
export const detectTableFormat = (text) => {
  const trimmedText = text.trim();

  // Check for HTML table (highest priority for Excel/Google Sheets)
  if (/<table/i.test(trimmedText) && /<\/table>/i.test(trimmedText)) {
    return 'html';
  }

  // Check for JSON (array of objects)
  if (trimmedText.startsWith('[') && trimmedText.endsWith(']')) {
    try {
      const data = JSON.parse(trimmedText);
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
        return 'json';
      }
    } catch (e) {
      // Not valid JSON
    }
  }

  const lines = trimmedText.split('\n').map(l => l.trim()).filter(Boolean);

  // Markdown table — only when the WHOLE text is a table. A valid separator row on
  // line 2 is necessary but NOT sufficient: if any non-empty line is not a table
  // row (i.e. has no `|`), the text is prose+table and must NOT be shredded here —
  // return null and let the caller render it as markdown (prose + native table).
  if (lines.length >= 2) {
    const header = lines[0];
    const separator = lines[1];
    // Reason: accept GFM separators with 2+ dashes and optional alignment colons
    // (`:-`, `-:`, `:--:`, `--`, `---`), not just the previous rigid 3+ dashes.
    const isSeparator = /^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(separator);
    if (header.includes('|') && isSeparator && lines.every(l => l.includes('|'))) {
      return 'md';
    }
  }

  // TSV (Tab-Separated Values) — Excel/Google Sheets copy as TSV by default.
  // Require ≥2 lines and a tab on EVERY non-empty line (a lone tab in a prose
  // paragraph, or tabs on only some lines, no longer misfires).
  if (lines.length >= 2 && lines.every(l => l.includes('\t'))) {
    return 'tsv';
  }

  // CSV — genuinely tabular comma data only (see doc comment above).
  if (lines.length >= 2 && lines.every(l => l.includes(','))) {
    const counts = lines.map(l => l.split(',').length);
    // Modal (most frequent) field count.
    const modal = counts.slice().sort(
      (a, b) => counts.filter(c => c === b).length - counts.filter(c => c === a).length
    )[0];
    const consistentRatio = counts.filter(c => c === modal).length / counts.length;
    const proseRatio = lines.filter(l => /[.!?:;]$/.test(l)).length / lines.length;
    if (modal >= 2 && consistentRatio >= 0.8 && proseRatio < 0.5) {
      return 'csv';
    }
  }

  return null;
};

/**
 * Decide how a chat message / clipboard string should be inserted into the editor.
 * Reason (T-018): both the chat Insert AND Replace buttons route through this ONE
 * classifier (via useTextInsertion), so they always behave identically. Keeping the
 * routing here — beside detectTableFormat and free of React/DOM deps — makes it
 * unit-testable and prevents the two paths from drifting apart.
 *
 * @param {string} text
 * @returns {{ action: 'table'|'html-table'|'raw-html'|'markdown'|'plaintext', format: string|null }}
 *   - table     : a pure table (md/csv/tsv/json) → show the convert-to-table dialog.
 *   - html-table: contains an HTML <table> → sanitize + insert as a native table.
 *   - raw-html  : other HTML markup → insert as-is.
 *   - markdown  : markdown-ish text (incl. prose+table mixes) → render to HTML so
 *                 surrounding prose becomes paragraphs and the table a native node.
 *   - plaintext : plain prose (even with commas) → insert verbatim, no dialog.
 */
export const classifyInsertion = (text) => {
  const trimmed = (text || '').trim();
  if (!trimmed) return { action: 'plaintext', format: null };

  const format = detectTableFormat(trimmed);
  if (format && format !== 'html') return { action: 'table', format };
  if (format === 'html' || /<table/i.test(trimmed)) return { action: 'html-table', format: 'html' };
  if (trimmed.startsWith('<')) return { action: 'raw-html', format: null };

  // Reason: same markdown sniff the insertion hook used inline — bold, headings,
  // lists, inline/blocks code, links, or a GFM separator row.
  const maybeMd = /\|\s*-{3,}\s*\||\*\*|^\s{0,3}#|^\s*[-*+]\s|`{1,3}|\[.+\]\(.+\)/m.test(trimmed);
  if (maybeMd) return { action: 'markdown', format: null };

  return { action: 'plaintext', format: null };
};
/**
 * Generate initial content for TipTap editor in ProseMirror JSON format
 * Reason: Convert parsed table data to TipTap-compatible structure
 */
export const generateInitialContent = (data, dataType) => {
  const { headers, body } = parseTableData(data, dataType);

  // Reason: Handle empty table case with default structure
  if (headers.length === 0 && body.length === 0) {
    return {
      type: 'doc',
      content: [
        {
          type: 'table',
          content: [
            {
              type: 'tableRow',
              content: [
                {
                  type: 'tableHeader',
                  content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Header 1' }] }],
                },
              ],
            },
            {
              type: 'tableRow',
              content: [
                {
                  type: 'tableCell',
                  content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Body Cell 1' }] }],
                },
              ],
            },
          ],
        },
      ],
    };
  }

  // Reason (T-014 item B8): a cell may be a plain string (csv/tsv/json/md) or a
  // `{ text, colspan, rowspan }` object (html, which preserves merges). Build a
  // native tableHeader/tableCell node carrying colspan/rowspan attrs when > 1.
  const buildCell = (cell, type) => {
    const isObj = cell !== null && typeof cell === 'object';
    const text = isObj ? cell.text : cell;
    const attrs = {};
    if (isObj && cell.colspan > 1) attrs.colspan = cell.colspan;
    if (isObj && cell.rowspan > 1) attrs.rowspan = cell.rowspan;
    return {
      type,
      ...(Object.keys(attrs).length ? { attrs } : {}),
      content: [{
        type: 'paragraph',
        content: text ? [{ type: 'text', text: String(text) }] : [],
      }],
    };
  };

  // Reason: Build header row with proper structure
  const headerRow = {
    type: 'tableRow',
    content: headers.map(header => buildCell(header, 'tableHeader')),
  };

  // Reason: Build body rows with proper structure
  const bodyRows = body.map(row => ({
    type: 'tableRow',
    content: row.map(cell => buildCell(cell, 'tableCell')),
  }));

  return {
    type: 'doc',
    content: [
      {
        type: 'table',
        content: [headerRow, ...bodyRows],
      },
    ],
  };
};